import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../context/AuthContext';
import AppLayout from '../../components/ui/AppLayout';
import HeaderBar from '../../components/ui/HeaderBar';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Icon from '../../components/AppIcon';
import { userAPI, recruiterAPI, biodataAPI, employerAPI, clabAPI } from '../../utils/api';
import { useBiodataCache } from '../../context/BiodataCacheContext';
import { useNotifier } from '../../context/NotificationContext';

const MasterAdminDashboard = () => {
  const notify = useNotifier();
  const { user } = useAuth();
  const { getAllBiodatas } = useBiodataCache();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    stats: {
      totalUsers: 0,
      activeUsers: 0,
      totalRecruiters: 0,
      activeRecruiters: 0,
      totalBiodata: 0,
      pendingBiodata: 0,
      approvedBiodata: 0,
      rejectedBiodata: 0,
      totalEmployers: 0,
      unselectedWorkers: 0
    },
    recentActivity: [],
    systemHealth: {
      apiStatus: 'healthy',
      lastUpdate: new Date(),
      errorRate: 0.2
    }
  });

  const breadcrumbItems = [
    { label: 'Master Admin Dashboard', path: '/master-admin-dashboard' }
  ];

  const normalizeCollectionResponse = (payload) => {
    if (!payload) {
      return { items: [], total: 0, pagination: {} };
    }

    const pagination =
      payload.pagination ||
      payload.data?.pagination ||
      {};

    let items = [];

    if (Array.isArray(payload.records)) {
      items = payload.records;
    } else if (Array.isArray(payload.data?.data)) {
      items = payload.data.data;
    } else if (Array.isArray(payload.data)) {
      items = payload.data;
    } else if (Array.isArray(payload)) {
      items = payload;
    }

    const totalSource = pagination?.total;
    const total = typeof totalSource === 'number'
      ? totalSource
      : typeof totalSource === 'bigint'
        ? Number(totalSource)
        : typeof totalSource === 'string'
          ? parseInt(totalSource, 10) || items.length
          : items.length;

    return {
      items,
      total,
      pagination
    };
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      const requestStatuses = [];
      const fetchSafely = async (label, fn) => {
        try {
          const data = await fn();
          requestStatuses.push({ label, success: true });
          return { data, success: true };
        } catch (err) {
          console.error(`Failed to fetch ${label}:`, err);
          requestStatuses.push({ label, success: false });
          return { data: null, success: false };
        }
      };

      const usersResult = await fetchSafely('users', () => userAPI.getAllUsers());
      const recruitersResult = await fetchSafely('recruiters', () => recruiterAPI.getAllRecruiters());
      const biodataResult = await fetchSafely('biodata', () => getAllBiodatas());
      if (biodataResult.data?.fromCache) {
      }
      const employersResult = await fetchSafely('employers', () => employerAPI.getAllEmployers());
      const biodataStatusCountsResult = await fetchSafely('biodataStatusCounts', () => biodataAPI.getStatusCounts());
      const clabDashboardResult = await fetchSafely('clabDashboard', () => clabAPI.dashboard());

      const normalizeResult = (result) =>
        result.success && result.data
          ? normalizeCollectionResponse(result.data)
          : { items: [], total: 0, pagination: {} };

      const usersData = normalizeResult(usersResult);
      const recruitersData = normalizeResult(recruitersResult);
      const biodataData = normalizeResult(biodataResult);
      const employersData = normalizeResult(employersResult);

      const safeNormalizeStatus = (statusValue) => {
        if (typeof statusValue === 'string') return statusValue.toLowerCase();
        if (statusValue === null || statusValue === undefined) return '';
        return String(statusValue).toLowerCase();
      };

      const defaultStatusCounts = {
        drafts: 0,
        submitted: 0,
        approved: 0,
        rejected: 0,
        selected: 0,
        resubmitted: 0
      };

      const statusCountsAvailable =
        biodataStatusCountsResult.success &&
        biodataStatusCountsResult.data?.status === 'success';

      const biodataStatusCounts = statusCountsAvailable
        ? {
            ...defaultStatusCounts,
            ...biodataStatusCountsResult.data.data
          }
        : defaultStatusCounts;

      const totalBiodataFromStatuses = statusCountsAvailable
        ? Object.values(biodataStatusCounts).reduce((sum, value) => {
            const numericValue = Number(value);
            return sum + (Number.isFinite(numericValue) ? numericValue : 0);
          }, 0)
        : 0;

      const activeUsers = usersData.items.filter(
        (u) => safeNormalizeStatus(u.status) === 'active'
      ).length;

      // Recruiter records currently do not include user status information;
      // treat all returned recruiters as active unless an explicit status says otherwise.
      const activeRecruiters = recruitersData.items.filter((r) => {
        if (r?.users && Array.isArray(r.users)) {
          return r.users.some((user) => safeNormalizeStatus(user.status) === 'active');
        }
        if (typeof r?.status === 'string') {
          return safeNormalizeStatus(r.status) !== 'inactive';
        }
        return true;
      }).length;

      const clabDashboardStats = clabDashboardResult.success
        ? clabDashboardResult.data?.data || clabDashboardResult.data
        : null;

      const unselectedWorkers = Number(clabDashboardStats?.biodata_stats?.unselected || 0);

      const pendingBiodata = statusCountsAvailable
        ? Number(biodataStatusCounts.submitted || 0)
        : biodataData.items.filter((b) => safeNormalizeStatus(b.status) === 'pending').length;
      const approvedBiodata = statusCountsAvailable
        ? Number(biodataStatusCounts.approved || 0)
        : biodataData.items.filter((b) => safeNormalizeStatus(b.status) === 'approved').length;
      const rejectedBiodata = statusCountsAvailable
        ? Number(biodataStatusCounts.rejected || 0)
        : biodataData.items.filter((b) => safeNormalizeStatus(b.status) === 'rejected').length;

      const failedRequests = requestStatuses.filter((result) => !result.success);

      // Update dashboard data
      setDashboardData(prev => ({
        ...prev,
        stats: {
          totalUsers: usersData.total || usersData.items.length,
          activeUsers,
          totalRecruiters: recruitersData.total || recruitersData.items.length,
          activeRecruiters,
          totalBiodata: totalBiodataFromStatuses || biodataData.total || biodataData.items.length,
          pendingBiodata,
          approvedBiodata,
          rejectedBiodata,
          totalEmployers: employersData.total || employersData.items.length,
          unselectedWorkers
        },
        recentActivity: generateRecentActivity(
          usersData.items,
          recruitersData.items,
          biodataData.items
        ),
        systemHealth: {
          apiStatus: failedRequests.length ? 'warning' : 'healthy',
          lastUpdate: new Date(),
          errorRate: failedRequests.length
            ? (failedRequests.length / 5) * 100
            : Math.random() * 2 // Simulate error rate when all good
        }
      }));

    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      setDashboardData(prev => ({
        ...prev,
        systemHealth: {
          apiStatus: 'error',
          lastUpdate: new Date(),
          errorRate: 5.0
        }
      }));
    } finally {
      setLoading(false);
    }
  };

  const generateRecentActivity = (users, recruiters, biodata) => {
    const activities = [];
    
    // Recent users (last 5)
    users.slice(-5).reverse().forEach(user => {
      activities.push({
        id: `user-${user.id}`,
        type: 'user_created',
        title: 'New User Created',
        description: `${user.full_name} (${user.role}) was added to the system`,
        timestamp: user.created_at,
        icon: 'UserPlus',
        color: 'text-primary'
      });
    });

    // Recent recruiters (last 3)
    recruiters.slice(-3).reverse().forEach(recruiter => {
      activities.push({
        id: `recruiter-${recruiter.id}`,
        type: 'recruiter_created',
        title: 'New Recruiter Registered',
        description: `${recruiter.name} from ${recruiter.country}`,
        timestamp: recruiter.created_at,
        icon: 'Building',
        color: 'text-accent'
      });
    });

    // Recent biodata submissions (last 5)
    biodata.slice(-5).reverse().forEach(bio => {
      activities.push({
        id: `biodata-${bio.id}`,
        type: 'biodata_submitted',
        title: 'Biodata Submitted',
        description: `${bio.full_name} - ${bio.passport_no}`,
        timestamp: bio.created_at,
        icon: 'FileText',
        color: 'text-success'
      });
    });

    // Sort by timestamp and return last 10
    return activities
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      .slice(0, 10);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return 'Invalid Date';
    
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(dateObj);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return 'text-success';
      case 'warning': return 'text-warning';
      case 'error': return 'text-error';
      default: return 'text-secondary-500';
    }
  };

  const StatCard = ({ title, value, icon, color, subtitle, change }) => (
    <div className="bg-white rounded-lg border border-secondary-200 p-6 hover:shadow-lg transition-shadow duration-200">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-secondary-600">{title}</p>
          <p className="text-2xl font-bold text-text-primary mt-1">
            {loading ? (
              <div className="animate-pulse bg-secondary-200 h-8 w-16 rounded"></div>
            ) : (
              formatNumber(value)
            )}
          </p>
          {subtitle && (
            <p className="text-xs text-secondary-500 mt-1">{subtitle}</p>
          )}
        </div>
        <div className={`p-3 rounded-full ${color} bg-opacity-10`}>
          <Icon name={icon} size={24} className={color} />
        </div>
      </div>
      {change && (
        <div className="mt-4 flex items-center">
          <Icon 
            name={change.value >= 0 ? 'TrendingUp' : 'TrendingDown'} 
            size={16} 
            className={change.value >= 0 ? 'text-success' : 'text-error'} 
          />
          <span className={`text-sm ml-1 ${change.value >= 0 ? 'text-success' : 'text-error'}`}>
            {Math.abs(change.value)}% {change.period}
          </span>
        </div>
      )}
    </div>
  );

  const QuickActionCard = ({ title, description, icon, color, onClick, disabled = false }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`w-full text-left bg-white rounded-lg border border-secondary-200 p-4 hover:shadow-md transition-all duration-200 ${
        disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-primary-300'
      }`}
    >
      <div className="flex items-center space-x-3">
        <div className={`p-2 rounded-lg ${color} bg-opacity-10`}>
          <Icon name={icon} size={20} className={color} />
        </div>
        <div>
          <h3 className="font-medium text-text-primary">{title}</h3>
          <p className="text-sm text-secondary-600">{description}</p>
        </div>
      </div>
    </button>
  );

  const ActivityItem = ({ activity }) => (
    <div className="flex items-start space-x-3 p-3 hover:bg-secondary-50 rounded-lg transition-colors duration-150">
      <div className={`p-2 rounded-full ${activity.color} bg-opacity-10 flex-shrink-0`}>
        <Icon name={activity.icon} size={16} className={activity.color} />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-medium text-text-primary">{activity.title}</h4>
        <p className="text-sm text-secondary-600 truncate">{activity.description}</p>
        <p className="text-xs text-secondary-500 mt-1">{formatDate(activity.timestamp)}</p>
      </div>
    </div>
  );

  return (
    <AppLayout sidebarCollapsed={sidebarCollapsed} setSidebarCollapsed={setSidebarCollapsed}>
      <Helmet>
        <title>Master Admin Dashboard - eBiodata CLAB</title>
      </Helmet>

      <div className="flex-1 flex flex-col min-h-screen bg-secondary-50">
        <HeaderBar 
          userRole={user?.role || 'admin'}
          userName={user?.name || user?.full_name || 'Master Admin'}
          onSidebarToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
          sidebarCollapsed={sidebarCollapsed}
          setSidebarCollapsed={setSidebarCollapsed}
        />
        
        <main className="flex-1 p-6">
          <div className="max-w-7xl mx-auto">
            <BreadcrumbNavigation items={breadcrumbItems} />
            
            {/* Header */}
            <div className="mb-8">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-text-primary">Master Admin Dashboard</h1>
                  <p className="text-secondary-600 mt-1">
                    Welcome back, {user?.name}! Here's your system overview.
                  </p>
                </div>
                <button
                  onClick={loadDashboardData}
                  disabled={loading}
                  className="inline-flex items-center px-4 py-2 border border-secondary-300 rounded-lg text-sm font-medium text-secondary-700 bg-white hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                >
                  <Icon name={loading ? 'Loader2' : 'RefreshCw'} size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Refresh Data
                </button>
              </div>
            </div>

            {/* System Health Alert */}
            {dashboardData.systemHealth.apiStatus !== 'healthy' && (
              <div className="mb-6 bg-error-50 border border-error-200 rounded-lg p-4">
                <div className="flex items-center">
                  <Icon name="AlertTriangle" size={20} className="text-error mr-3" />
                  <div>
                    <h3 className="text-sm font-medium text-error-800">System Health Alert</h3>
                    <p className="text-sm text-error-700 mt-1">
                      Some API services are experiencing issues. Error rate: {dashboardData.systemHealth.errorRate.toFixed(1)}%
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <StatCard
                title="Total Users"
                value={dashboardData.stats.totalUsers}
                icon="Users"
                color="text-primary"
                subtitle={`${dashboardData.stats.activeUsers} active`}
              />
              <StatCard
                title="Recruiters"
                value={dashboardData.stats.totalRecruiters}
                icon="Building"
                color="text-accent"
                subtitle={`${dashboardData.stats.activeRecruiters} active`}
              />
              <StatCard
                title="Total Biodata"
                value={dashboardData.stats.totalBiodata}
                icon="FileText"
                color="text-success"
                subtitle={`${dashboardData.stats.pendingBiodata} pending review`}
              />
              <StatCard
                title="Employers"
                value={dashboardData.stats.totalEmployers}
                icon="Briefcase"
                color="text-warning"
                subtitle="External system"
              />
            </div>

            {/* Biodata Status Overview */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
              <StatCard
                title="Pending Reviews"
                value={dashboardData.stats.pendingBiodata}
                icon="Clock"
                color="text-warning"
                subtitle="Awaiting CLAB review"
              />
              <StatCard
                title="Approved"
                value={dashboardData.stats.approvedBiodata}
                icon="CheckCircle"
                color="text-success"
                subtitle="Ready for selection"
              />
              <StatCard
                title="Rejected"
                value={dashboardData.stats.rejectedBiodata}
                icon="XCircle"
                color="text-error"
                subtitle="Require corrections"
              />
              <StatCard
                title="Unselected Workers"
                value={dashboardData.stats.unselectedWorkers}
                icon="Users"
                color="text-indigo-600"
                subtitle="Available to employers"
              />
            </div>

            {/* Quick Actions & Recent Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Quick Actions */}
              <div className="bg-white rounded-lg border border-secondary-200 p-6">
                <h2 className="text-lg font-semibold text-text-primary mb-4">Quick Actions</h2>
                <div className="space-y-3">
                  <QuickActionCard
                    title="User Management"
                    description="Create, edit, and manage system users"
                    icon="UserCog"
                    color="text-primary"
                    onClick={() => window.location.href = '/user-management'}
                  />
                  <QuickActionCard
                    title="Recruiter Management"
                    description="Manage recruiter agencies and profiles"
                    icon="Building2"
                    color="text-accent"
                    onClick={() => window.location.href = '/recruiter-management'}
                  />
                  <QuickActionCard
                    title="System Settings"
                    description="Configure system parameters and policies"
                    icon="Settings"
                    color="text-secondary-600"
                    onClick={() => notify.info('System Settings - Coming Soon')}
                    disabled={true}
                  />
                  <QuickActionCard
                    title="Reports & Analytics"
                    description="View comprehensive system reports"
                    icon="BarChart3"
                    color="text-success"
                    onClick={() => window.location.href = '/clab-reports-analytics'}
                  />
                </div>
              </div>

              {/* Recent Activity */}
              <div className="bg-white rounded-lg border border-secondary-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-text-primary">Recent Activity</h2>
                  <button className="text-sm text-primary hover:text-primary-700 font-medium">
                    View All
                  </button>
                </div>
                <div className="space-y-1 max-h-96 overflow-y-auto">
                  {loading ? (
                    <div className="space-y-3">
                      {[...Array(5)].map((_, i) => (
                        <div key={i} className="animate-pulse flex items-center space-x-3">
                          <div className="w-8 h-8 bg-secondary-200 rounded-full"></div>
                          <div className="flex-1">
                            <div className="h-4 bg-secondary-200 rounded mb-1"></div>
                            <div className="h-3 bg-secondary-200 rounded w-3/4"></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : dashboardData.recentActivity.length > 0 ? (
                    dashboardData.recentActivity.map(activity => (
                      <ActivityItem key={activity.id} activity={activity} />
                    ))
                  ) : (
                    <div className="text-center py-8">
                      <Icon name="Activity" size={32} className="text-secondary-400 mx-auto mb-2" />
                      <p className="text-secondary-600">No recent activity</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* System Info Footer */}
            <div className="mt-8 bg-white rounded-lg border border-secondary-200 p-4">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center">
                    <div className={`w-2 h-2 rounded-full mr-2 ${
                      dashboardData.systemHealth.apiStatus === 'healthy' ? 'bg-success' : 'bg-error'
                    }`}></div>
                    <span className="text-secondary-600">
                      System Status: <span className={getStatusColor(dashboardData.systemHealth.apiStatus)}>
                        {dashboardData.systemHealth.apiStatus}
                      </span>
                    </span>
                  </div>
                  <span className="text-secondary-500">
                    Last Updated: {formatDate(dashboardData.systemHealth.lastUpdate)}
                  </span>
                </div>
                <span className="text-secondary-500">
                  eBiodata CLAB v2.0.0
                </span>
              </div>
            </div>
          </div>
        </main>
      </div>
    </AppLayout>
  );
};

export default MasterAdminDashboard;
